import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Chip,
  Avatar,
  IconButton,
  Alert,
  useTheme,
  useMediaQuery,
  TextField,
  InputAdornment,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Skeleton,
} from "@mui/material";
import {
  Search,
  Trash2,
  FileText,
  CheckCircle2,
  XCircle,
  Clock,
  AlertCircle,
  Images,
  Sparkles,
  X,
  Rocket,
} from "lucide-react";
import useApiEp from "../../hooks/useApiEp";
import BackBtn from "../../widgets/BackBtn";
import { API_ENDPOINT } from "../../utils/constants";
import { getHeader } from "../../utils/util";
import {
  RcsTemplatePreview,
  getTemplateName,
  getTemplateStatus,
  getTemplateTypeLabel,
} from "./RcsTemplatePreview";

// Helper for status styling
const getStatusProps = (status) => {
  const s = (status || "pending").toLowerCase();
  switch (s) {
    case "approved":
      return { color: "success", icon: <CheckCircle2 size={16} /> };
    case "rejected":
      return { color: "error", icon: <XCircle size={16} /> };
    case "pending":
      return { color: "warning", icon: <Clock size={16} /> };
    default:
      return { color: "default", icon: <Clock size={16} /> };
  }
};

const isCarouselTemplate = (template) =>
  template?.templateType === "carouselCard" ||
  Array.isArray(template?.content?.cardContents) ||
  Boolean(template?.content?.richCardDetails?.carousel);

const RcsTemplates = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const handleSearchChange = (event) => {
    setSearchInput(event.target.value);
  };

  const { data, isLoading, isError, refetch } = useApiEp(
    `/rcs/get-templates?sq=${encodeURIComponent(searchQuery)}`
  );

  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);

  // Debounce search input
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      setSearchQuery(searchInput.trim());
    }, 400);
    return () => clearTimeout(debounceTimer);
  }, [searchInput]);

  // Memoize template data
  const templates = useMemo(() => (Array.isArray(data) ? data : []), [data]);

  // Auto-select the first template if none is selected
  useEffect(() => {
    setSelectedTemplate((currentTemplate) => {
      if (templates.length === 0) return null;
      const refreshedSelection = currentTemplate
        ? templates.find((template) => template._id === currentTemplate._id)
        : null;
      return refreshedSelection || templates[0];
    });
  }, [templates]);

  const handleDelete = async () => {
    if (!templateToDelete) return;

    try {
      await fetch(
        `${API_ENDPOINT}/rcs/delete-template/${templateToDelete._id}`,
        {
          method: "DELETE",
          ...getHeader(),
        }
      );
      refetch();
      if (selectedTemplate?._id === templateToDelete._id) {
        setSelectedTemplate(null);
      }
      setDeleteDialogOpen(false);
      setTemplateToDelete(null);
    } catch (err) {
      console.error("Error deleting template:", err);
      alert("Failed to delete template. Please try again.");
    }
  };

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        bgcolor: "#f8fafc",
        overflow: "hidden",
      }}
    >
      {/* 1. APP HEADER: Campaign Launcher Vibe */}
      <Box
        sx={{
          px: { xs: 2, md: 4 },
          py: 2,
          bgcolor: "white",
          borderBottom: "1px solid #e2e8f0",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexShrink: 0,
          zIndex: 10,
          boxShadow: "0 2px 10px rgba(0,0,0,0.02)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <BackBtn />
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              background: "linear-gradient(135deg, #6d4bd1, #4f46e5)",
              boxShadow: "0 4px 12px rgba(109, 75, 209, 0.2)",
            }}
          >
            <Rocket size={20} />
          </Box>
          <Box>
            <Typography
              variant="h6"
              fontWeight="800"
              color="#0f172a"
              lineHeight={1.2}
            >
              Template Library
            </Typography>
            <Typography variant="caption" color="#64748b">
              Select a design to launch your next campaign
            </Typography>
          </Box>
        </Box>

        <TextField
          size="small"
          variant="outlined"
          placeholder="Search templates..."
          value={searchInput}
          onChange={handleSearchChange}
          sx={{
            display: { xs: "none", md: "block" },
            width: 320,
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
              bgcolor: "#f1f5f9",
              "& fieldset": { borderColor: "transparent" },
              "&:hover fieldset": { borderColor: "#cbd5e1" },
              "&.Mui-focused fieldset": { borderColor: "#6d4bd1" },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={18} color="#64748b" />
              </InputAdornment>
            ),
            endAdornment: searchInput ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => {
                    setSearchInput("");
                    setSearchQuery("");
                  }}
                >
                  <X size={16} />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
      </Box>

      {/* 2. SPLIT LAYOUT: Forced Side-by-side App Workspace */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          flex: 1,
          overflow: "hidden",
          width: "100%",
        }}
      >
        {/* LEFT PANEL: Template Selector (Fixed Width on Desktop) */}
        <Box
          sx={{
            width: { xs: "100%", md: "400px", lg: "450px" },
            borderRight: { md: "1px solid #e2e8f0" },
            bgcolor: "white",
            display: "flex",
            flexDirection: "column",
            flexShrink: 0,
            height: {
              xs: isMobile && selectedTemplate ? "45vh" : "100%",
              md: "100%",
            },
            borderBottom: { xs: "1px solid #e2e8f0", md: "none" },
          }}
        >
          {/* Mobile Search - Only visible on small screens */}
          {isMobile && (
            <Box sx={{ p: 2, borderBottom: "1px solid #e2e8f0" }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search templates..."
                value={searchInput}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search size={18} color="#64748b" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          )}

          <Box
            sx={{
              px: 3,
              py: 2,
              borderBottom: "1px solid #e2e8f0",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="subtitle2" fontWeight="700" color="#475569">
              AVAILABLE TEMPLATES
            </Typography>
            <Chip
              label={templates.length}
              size="small"
              sx={{ bgcolor: "#f1f5f9", fontWeight: 700 }}
            />
          </Box>

          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              p: 2,
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
            }}
          >
            {isLoading ? (
              <TemplateListSkeleton />
            ) : isError ? (
              <Alert severity="error">Failed to load templates.</Alert>
            ) : templates.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 8 }}>
                <FileText
                  size={40}
                  color="#cbd5e1"
                  style={{ margin: "0 auto 12px" }}
                />
                <Typography color="text.secondary">
                  No templates found.
                </Typography>
              </Box>
            ) : (
              templates.map((template) => {
                const status = getTemplateStatus(template);
                const statusProps = getStatusProps(status);
                const carousel = isCarouselTemplate(template);
                const isSelected = selectedTemplate?._id === template._id;

                return (
                  <Paper
                    key={template._id}
                    elevation={0}
                    onClick={() => setSelectedTemplate(template)}
                    sx={{
                      p: 2,
                      cursor: "pointer",
                      border: "1px solid",
                      borderColor: isSelected ? "#6d4bd1" : "#e2e8f0",
                      borderRadius: 3,
                      bgcolor: isSelected ? "#f8f7ff" : "white",
                      transition: "all 0.2s ease-in-out",
                      position: "relative",
                      "&:hover": {
                        borderColor: isSelected ? "#6d4bd1" : "#cbd5e1",
                        transform: "translateY(-2px)",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.04)",
                      },
                    }}
                  >
                    <Box sx={{ display: "flex", gap: 2 }}>
                      <Avatar
                        sx={{
                          bgcolor: carousel
                            ? isSelected
                              ? "#fff"
                              : "#eaf8f2"
                            : isSelected
                            ? "#fff"
                            : "#ede9fe",
                          color: carousel ? "#087a55" : "#6d4bd1",
                          width: 44,
                          height: 44,
                          borderRadius: 2,
                          boxShadow: isSelected
                            ? "0 2px 8px rgba(0,0,0,0.05)"
                            : "none",
                        }}
                      >
                        {carousel ? (
                          <Images size={22} />
                        ) : (
                          <FileText size={22} />
                        )}
                      </Avatar>

                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="subtitle1"
                          fontWeight="700"
                          color="#0f172a"
                          noWrap
                        >
                          {getTemplateName(template)}
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mt: 0.5,
                          }}
                        >
                          <Chip
                            label={getTemplateTypeLabel(template).toUpperCase()}
                            size="small"
                            sx={{
                              fontSize: "10px",
                              height: 20,
                              fontWeight: 600,
                              bgcolor: "#f1f5f9",
                              color: "#64748b",
                            }}
                          />
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                              color: `${statusProps.color}.main`,
                            }}
                          >
                            {statusProps.icon}
                            <Typography
                              variant="caption"
                              sx={{
                                fontWeight: 600,
                                textTransform: "capitalize",
                              }}
                            >
                              {status}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>

                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "space-between",
                        }}
                      >
                        <IconButton
                          size="small"
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation();
                            setTemplateToDelete(template);
                            setDeleteDialogOpen(true);
                          }}
                          sx={{
                            opacity: isSelected ? 1 : 0.4,
                            "&:hover": { opacity: 1, bgcolor: "#fee2e2" },
                          }}
                        >
                          <Trash2 size={16} />
                        </IconButton>
                      </Box>
                    </Box>
                  </Paper>
                );
              })
            )}
          </Box>
        </Box>

        {/* RIGHT PANEL: Live Preview Canvas (Takes up remaining space) */}
        {(!isMobile || (isMobile && selectedTemplate)) && (
          <Box
            sx={{
              flex: 1,
              bgcolor: "#f8fafc",
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              backgroundImage: "radial-gradient(#e2e8f0 1px, transparent 1px)",
              backgroundSize: "24px 24px",
              p: { xs: 2, md: 4 },
            }}
          >
            {selectedTemplate ? (
              <Box sx={{ width: "100%", maxWidth: 800, mx: "auto" }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 3,
                    alignItems: "center",
                  }}
                >
                  <Typography variant="h6" fontWeight="700" color="#334155">
                    Live Preview
                  </Typography>
                  <Button
                    variant="contained"
                    sx={{
                      bgcolor: "#0f172a",
                      textTransform: "none",
                      borderRadius: 2,
                      px: 3,
                      "&:hover": { bgcolor: "#1e293b" },
                    }}
                  >
                    Use Template
                  </Button>
                </Box>
                <RcsTemplatePreview template={selectedTemplate} />
              </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  textAlign: "center",
                }}
              >
                <Sparkles
                  size={64}
                  color="#cbd5e1"
                  style={{ marginBottom: 16 }}
                />
                <Typography
                  variant="h5"
                  fontWeight="700"
                  color="#475569"
                  gutterBottom
                >
                  Select a template to preview
                </Typography>
                <Typography color="#94a3b8" maxWidth={400}>
                  Choose a template from the library on the left to see exactly
                  how it will appear on your customers' devices.
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <AlertCircle size={24} color="#d32f2f" />
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the template{" "}
            <strong>"{templateToDelete?.templateName}"</strong>? This action
            cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            color="error"
            disableElevation
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// Skeleton Loader component
const TemplateListSkeleton = () => (
  <>
    {[...Array(5)].map((_, index) => (
      <Paper
        key={index}
        elevation={0}
        sx={{ p: 2, border: "1px solid #e2e8f0", borderRadius: 3 }}
      >
        <Box sx={{ display: "flex", gap: 2 }}>
          <Skeleton
            variant="rectangular"
            width={44}
            height={44}
            sx={{ borderRadius: 2 }}
          />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="80%" height={24} />
            <Skeleton variant="text" width="40%" height={20} />
          </Box>
        </Box>
      </Paper>
    ))}
  </>
);

export default RcsTemplates;
